'use client';

import { ErrorInfo, MouseEvent, RefObject, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import trackOpenMixMatchBeam from '@helpers/analyticsHelpers/trackOpenMixMatchBeam';
import { getValidProductLabels, isProductLabelKey } from '@helpers/productHelper';
import { getPromotionComparePrice } from '@helpers/productPromotion';
import slugify from '@helpers/slugify';
import Text from '@atoms/Text/Text';
import ProductQuantityControl from '@molecules/ProductQuantityControl/ProductQuantityControl';
import MixMatchBeam from '@molecules/MixMatchBeam/MixMatchBeam';
import PriceLabel from '@molecules/PriceLabel/PriceLabel';
import EnergyLabel from '@atoms/EnergyLabel/EnergyLabel';
import ErrorBoundary from '@utility/ErrorBoundary/ErrorBoundary';
import logger from '@logger';
import { isOfflineProduct, isOfflinePromotion } from '@helpers/typeGuards/product';
import paths from '@constants/paths';
import Button from '@atoms/Button/Button';
import {
  StyledImageWrapper,
  StyledImageLink,
  StyledLowestHistoricalPrice,
  StyledOfflinePromotionLowestHistoricalPrice,
  StyledProduct,
  StyledProductAddToList,
  StyledProductComparePrice,
  StyledProductEnergyWrapper,
  StyledProductFooter,
  StyledProductFooterBottomSection,
  StyledProductHeader,
  StyledProductLinkWrapper,
  StyledProductManufacturerBrand,
  StyledProductManufacturerVolumeWrapper,
  StyledProductName,
  StyledProductOriginalPrice,
  StyledProductPriceWrapper,
  StyledProductPromotionPriceInfo,
  StyledProductPromotionsPreviewCode,
  StyledProductUsedPromotion,
  StyledProductZCodes,
  StyledProductZCodesIcon,
} from './Product.styles';
import type { AxfoodBasicProductViewModel, AxfoodProductDetailsViewModel } from '@occ/api-client';
import { useProductImage } from '@hooks/useProductImage';
import AddToListComponent from '@organisms/AddToListComponent/AddToListComponent';
import { makeSingleOfflineEntry, makeSingleOnlineEntry } from '@helpers/wishlistProductEntries';
import trackShoppingList from '@helpers/analyticsHelpers/trackShoppingList';
import Icon from '@atoms/Icon/Icon';
import AddToList from '@icons/add-to-list.svg';
import { useProductLabelSplash } from '@hooks/useProductLabelSplash';
import ProductLabelSplash from '@molecules/ProductLabelSplash/ProductLabelSplash';
import useSymplifyExperiment from '@utility/ABTest/Symplify/useSymplifyExperiment';
import { buildUrl } from '@helpers/buildUrl';

export type Variant = 'product' | 'mixmatch' | 'multisearch';

export interface ProductProps {
  product: AxfoodProductDetailsViewModel | AxfoodBasicProductViewModel;
  category?: string;
  offline?: boolean;
  variant?: Variant;
  listPos?: number;
  openMixMatchBeam?: string;
  setOpenMixMatchBeam?: (code: string) => void;
  gridRef?: RefObject<HTMLDivElement | null>;
  disableMixMatchButton?: boolean;
  hideAddToList?: boolean;
  className?: string;
  trackedComponentRef?: (node: HTMLDivElement | null) => void;
  isPage?: boolean;
}

const errorHandler = (code: string) => (error: Error, info: ErrorInfo) => {
  logger.error({
    error: `Product (${code}) Error boundary caught error`,
    message: error.message,
    ...info,
  });
};

const Product = ({
  product,
  category,
  offline,
  variant = 'product',
  listPos = 0,
  openMixMatchBeam,
  setOpenMixMatchBeam,
  gridRef,
  disableMixMatchButton = false,
  hideAddToList = false,
  className,
  trackedComponentRef,
  isPage,
}: ProductProps) => {
  // Internal ref for MixMatchBeam positioning calculations
  const productRef = useRef<HTMLDivElement | null>(null);

  // Update both internal ref and forwarded ref with the DOM node
  const handleRef = useCallback(
    (node: HTMLDivElement | null) => {
      productRef.current = node;
      // Forward the ref to TrackedComponent
      if (trackedComponentRef) trackedComponentRef(node);
    },
    [trackedComponentRef]
  );

  const router = useRouter();
  const pathname = usePathname();
  const t = useTranslations('product');
  // Generic derivations; the offline/bargain/preview special cases stay local below.
  const promotion = product?.potentialPromotions?.[0];
  const hasMixMatch = promotion?.realMixAndMatch === true;
  const hasPromotion = !!promotion;
  const hasComparePrices = !!(product?.comparePrice !== '' && product?.comparePriceUnit);
  const hasPromotionComparePrices = !!promotion?.comparePrice;
  const [isPromotionUsed, setIsPromotionUsed] = useState(false);
  const isPromotionPage = !!pathname?.startsWith('/erbjudanden');
  const isPromotionPreviewPage = pathname?.indexOf('/preview') !== -1;
  const lowestHistoricalPrice = promotion?.lowestHistoricalPrice;
  const redeemLimitLabel = promotion?.redeemLimitLabel || '';
  const offlinePromotionLowestHistoricalPriceValue =
    isOfflinePromotion(promotion) && isOfflineProduct(product) && product?.offlinePromotionLowestHistoricalPrice;
  const { productImageURL, productImageAltText, onImageLoadError } = useProductImage(product);
  const [addToListModalOpen, setAddToListModalOpen] = useState(false);
  const testVariant = useSymplifyExperiment('Mix Match Products', 'Original');

  const url = `${isPromotionPage || isPromotionPreviewPage ? (offline ? 'offline-' : 'online-') : ''}${slugify(
    product.name
  )}-${offline ? promotion?.code : product.code}`;
  let productUrl = `/produkt/${encodeURI(url)}`;

  if (isPromotionPreviewPage) {
    productUrl = `/erbjudanden/preview/${encodeURI(url)}`;
  } else if (isPromotionPage) {
    productUrl = `/erbjudanden/${encodeURI(url)}`;
  }

  const [zCodes, setZCodes] = useState<string[]>([]);
  const [showMixMatchBeam, setShowMixMatchBeam] = useState(false);
  const [startCloseAnimation, setStartCloseAnimation] = useState(false);
  const [quantity, setQuantity] = useState(0);

  useEffect(() => {
    if (testVariant === 'B') return;

    if (openMixMatchBeam === product.code) {
      setShowMixMatchBeam(true);
    } else {
      setShowMixMatchBeam(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openMixMatchBeam]);

  useEffect(() => {
    if (
      promotion &&
      isOfflinePromotion(promotion) &&
      promotion?.redeemLimit &&
      promotion?.redeemLimit >= 0 &&
      promotion?.timesUsed
    ) {
      setIsPromotionUsed(promotion.timesUsed >= promotion.redeemLimit);
    }
  }, [promotion]);

  const mixMatchOnClickHandler = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      if (testVariant === 'B') {
        // Open PDP in modal and signal ProductDetails to scroll to related products
        router.push(buildUrl(productUrl, { scrollTo: 'related' }));
      } else {
        if (openMixMatchBeam === product.code) {
          setStartCloseAnimation(true);
        } else {
          setStartCloseAnimation(false);
          if (setOpenMixMatchBeam) setOpenMixMatchBeam(product.code);
          trackOpenMixMatchBeam(product.code);
        }
      }
    },
    [testVariant, router, productUrl, openMixMatchBeam, product.code, setOpenMixMatchBeam]
  );

  const mixMatchOverride = useMemo(() => {
    if (!hasMixMatch || variant === 'mixmatch' || disableMixMatchButton) return undefined;
    return isOfflineProduct(product) ? (
      <ProductLabelSplash type="mixmatch" />
    ) : (
      <Button variant="link" size="small" onClick={mixMatchOnClickHandler}>
        {t('showMoreVariants')}
      </Button>
    );
  }, [hasMixMatch, variant, disableMixMatchButton, product, mixMatchOnClickHandler, t]);

  const productLabelSplash = useProductLabelSplash({
    product,
    mixMatchOverride,
  });

  const closeMixMatch = () => {
    setShowMixMatchBeam(false);
    if (setOpenMixMatchBeam) setOpenMixMatchBeam('');
  };

  useEffect(() => {
    setZCodes(getValidProductLabels(product, 3));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isPreview = useMemo(() => pathname?.indexOf(paths.PDP_PREVIEW) !== -1, [pathname]);

  let manufacturer = (product?.manufacturer || '').trim();
  // Fix problem where admins add a period instead of empty string
  if (manufacturer === '.') manufacturer = '';
  const displayVolume = useMemo(() => (product?.displayVolume || '').trim(), [product?.displayVolume]);

  return (
    <ErrorBoundary errorHandler={errorHandler(product.code)}>
      <StyledProduct
        data-testid="product"
        $offline={offline}
        $variant={variant}
        className={className}
        itemScope
        itemType="https://schema.org/Product"
        ref={handleRef}
      >
        {isPromotionUsed && (
          <StyledProductUsedPromotion>
            <Text type="label">{t('promotion.used')}</Text>
          </StyledProductUsedPromotion>
        )}

        <StyledProductHeader>
          {/* When inside a product modal (!isPage), use replace so clicking a related product replaces
              the current modal history entry rather than stacking another one — pressing back then exits
              cleanly to the listing page instead of looping through product modals. */}
          <StyledImageLink
            href={productUrl}
            scroll={false}
            replace={isPage === false}
            prefetch={false}
            aria-label={`${t('openProductDetailLabel')} ${product.name}`}
          >
            <StyledProductLinkWrapper>
              <StyledImageWrapper>
                <Image
                  priority
                  unoptimized
                  itemProp="image"
                  alt={productImageAltText}
                  src={productImageURL}
                  width="132"
                  height="132"
                  onError={onImageLoadError}
                />
              </StyledImageWrapper>
            </StyledProductLinkWrapper>
          </StyledImageLink>
          {!isPreview && !hideAddToList && (
            <StyledProductAddToList>
              <Button
                data-testid="open-add-to-list-button"
                onClick={() => setAddToListModalOpen(true)}
                variant="transparent"
                title={t('addToListLabel')}
                aria-label={t('addToListLabel')}
              >
                <Icon svg={AddToList} size={20} color="lighterBlack" />
              </Button>

              <AddToListComponent
                product={product}
                tracking={{ parent: 'product_list' }}
                addToListCallback={() => {
                  trackShoppingList({ action: 'current', parent: 'product_list' });
                }}
                createNewListCallback={() => {
                  trackShoppingList({ action: 'new', parent: 'product_list' });
                }}
                productEntries={
                  offline && isOfflineProduct(product)
                    ? [makeSingleOfflineEntry(product, quantity)]
                    : [makeSingleOnlineEntry(product, quantity)]
                }
                onClose={() => setAddToListModalOpen(false)}
                isOpen={addToListModalOpen}
              />
            </StyledProductAddToList>
          )}
          <StyledProductPriceWrapper>
            <PriceLabel product={product} showSavePrice="extended" />
          </StyledProductPriceWrapper>
          {product.energyDeclaration && (
            <StyledProductEnergyWrapper>
              <EnergyLabel
                energyDeclaration={product.energyDeclaration}
                onClick={() => {
                  router.push(buildUrl(productUrl, { tab: '3' }));
                }}
              />
            </StyledProductEnergyWrapper>
          )}
          <StyledProductZCodes>
            {zCodes.map((child) => (
              <div key={child}>
                <StyledProductZCodesIcon
                  src={`/icons/labels/${child}.svg`}
                  title={isProductLabelKey(child) ? t(`label.${child}`) : child}
                />
              </div>
            ))}
          </StyledProductZCodes>
          {productLabelSplash}
        </StyledProductHeader>

        <StyledProductFooter>
          <StyledProductName itemProp="name">{product.name}</StyledProductName>

          {(manufacturer || displayVolume) && (
            <StyledProductManufacturerVolumeWrapper>
              <StyledProductManufacturerBrand itemProp="brand">
                {manufacturer && manufacturer} {displayVolume && displayVolume}
              </StyledProductManufacturerBrand>
            </StyledProductManufacturerVolumeWrapper>
          )}

          <StyledProductFooterBottomSection>
            {hasPromotion && hasPromotionComparePrices && !offline && (
              <StyledProductPromotionPriceInfo>
                {`${t('price.compare')} ${getPromotionComparePrice(product)}`}

                {`${redeemLimitLabel ? ` • ${redeemLimitLabel}` : ''}`}
              </StyledProductPromotionPriceInfo>
            )}
            {hasPromotion && !offline && !product.bargainProduct && (
              <StyledProductOriginalPrice>
                {`${t('price.default')} ${product?.priceNoUnit} ${product?.priceUnit}`}
              </StyledProductOriginalPrice>
            )}
            {hasComparePrices && !product.bargainProduct && (
              <StyledProductComparePrice>
                {`${t('price.compare')} ${product?.comparePrice}/${product?.comparePriceUnit}`}
              </StyledProductComparePrice>
            )}
            {offline && hasPromotion && (
              <>
                {isOfflinePromotion(promotion) && promotion?.savePrice && (
                  <StyledProductPromotionPriceInfo>
                    {promotion?.savePrice}

                    {`${promotion?.redeemLimitLabel ? ` • ${promotion?.redeemLimitLabel}` : ''}`}
                  </StyledProductPromotionPriceInfo>
                )}
              </>
            )}
            {!!lowestHistoricalPrice && !product.bargainProduct && (
              <StyledLowestHistoricalPrice>
                {t('price.lowestHistorical', {
                  price: lowestHistoricalPrice?.formattedValue ?? '',
                })}
              </StyledLowestHistoricalPrice>
            )}
            {!!offlinePromotionLowestHistoricalPriceValue && (
              <StyledOfflinePromotionLowestHistoricalPrice>
                {t('price.offlinePromotionLowestHistoricalPrice', {
                  price: offlinePromotionLowestHistoricalPriceValue,
                })}
              </StyledOfflinePromotionLowestHistoricalPrice>
            )}
          </StyledProductFooterBottomSection>
          {/* Only show quantity input field when not in preview mode (promotional campaign preview) 
            and product is not offline */}
          {!isPreview && !offline && (
            <ProductQuantityControl
              product={product}
              variant="product"
              category={category}
              trackingSource={category}
              quantityCallback={setQuantity}
              mixmatch={variant === 'mixmatch'}
            />
          )}
          {isPreview && !!promotion?.code && (
            <StyledProductPromotionsPreviewCode>
              {t('promotionCode')} {promotion?.code}
            </StyledProductPromotionsPreviewCode>
          )}
        </StyledProductFooter>
        <div itemProp="offers" itemType="http://schema.org/Offer" itemScope>
          <link itemProp="url" href={productUrl} />
          <meta
            itemProp="availability"
            content={product.outOfStock ? 'https://schema.org/OutOfStock' : 'https://schema.org/InStock'}
          />
          <meta itemProp="priceCurrency" content="SEK" />
          <meta itemProp="price" content={product.priceNoUnit} />
        </div>
        <meta itemProp="description" content={product.productLine2} />
      </StyledProduct>
      {showMixMatchBeam && (
        <MixMatchBeam
          product={product}
          onClose={closeMixMatch}
          closeBeam={startCloseAnimation}
          listPos={listPos + 1}
          gridRef={gridRef}
          productRef={productRef}
        />
      )}
    </ErrorBoundary>
  );
};

export default Product;
